import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { buildGuestRegistrationLink, buildQrImageUrl } from "../../services/EventMasterService";

const GA_COLORS = {
  primary: "#8a5200",
  primaryDark: "#5a3600",
  accent: "#1e8a5a",
  accentDark: "#13613f",
  danger: "#b02a37",
  dangerDark: "#7f1d28",
  border: "#c27a2a",
  heading: "#4a2400",
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 700,
};

const gaFormSx = {
  "& .MuiInputLabel-root": {
    color: "#7a4a15",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: GA_COLORS.primary,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d3b994",
    },
    "&:hover fieldset": {
      borderColor: "#b9874e",
    },
    "&.Mui-focused fieldset": {
      borderColor: GA_COLORS.primary,
      borderWidth: "1px",
    },
  },
};

function EventDetailScreen({
  isEditing,
  selectedEvent,
  registrationSummary,
  mode,
  loading,
  canCreate,
  canEdit,
  canDelete,
  form,
  typeOptions,
  statusOptions,
  onBackToList,
  onCopy,
  onModify,
  onToggleStatus,
  onOpenBrochure,
  onPrintEvent,
  onPrintQrCode,
  onPrintRegistrationIds,
  onCopyGuestLink,
  onDelete,
  onCancel,
  onSubmit,
  onFieldChange,
  onAgeFieldChange,
  onAddAgeGroup,
  onRemoveAgeGroup,
  onPaymentModeFieldChange,
  onPaymentModeAttributeChange,
  onAddPaymentMode,
  onRemovePaymentMode,
}) {
  const [showMultiPrintDialog, setShowMultiPrintDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);

  const guestLink = selectedEvent?.eventID
    ? buildGuestRegistrationLink(selectedEvent.eventID)
    : "";

  function formatDate(value) {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString();
  }

  const ActionBar = () => (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
      {!isEditing && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={onBackToList}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Back
          </Button>
          {selectedEvent && canCreate && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCopy?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Copy
            </Button>
          )}
          {selectedEvent && canEdit && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onToggleStatus?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              {selectedEvent.status === "Confirmed" ? "Close" : "Confirm"}
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onPrintEvent?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Print
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowQrDialog(true)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              QR Code
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowMultiPrintDialog(true)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Multi Print IDs
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCopyGuestLink?.(selectedEvent)}
              disabled={
                loading ||
                !selectedEvent.onlineRegRequired ||
                selectedEvent.status !== "Confirmed"
              }
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Copy Guest Link
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onOpenBrochure?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              {selectedEvent.brochurePath ? "Brochure" : "Upload Brochure"}
            </Button>
          )}
          {selectedEvent && canEdit && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onModify?.(selectedEvent)}
              sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
            >
              Edit
            </Button>
          )}
          {selectedEvent && canDelete && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onDelete?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.danger, color: GA_COLORS.danger }}
            >
              Delete
            </Button>
          )}
        </>
      )}
      {isEditing && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={onBackToList}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="small"
            type="submit"
            form="event-master-form"
            disabled={loading}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            {loading ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Cancel
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <Paper
      elevation={2}
      sx={{ p: 2, border: `1px solid ${GA_COLORS.border}`, backgroundColor: "#fffdf9" }}
    >
      <ActionBar />
      <Divider sx={{ my: 2 }} />
      <Typography variant="h5" sx={{ mb: 2, color: GA_COLORS.heading, fontWeight: 700 }}>
        {isEditing
          ? mode === "create"
            ? "Create Event"
            : "Edit Event"
          : selectedEvent?.name || "Event Details"}
      </Typography>

      {!isEditing && !selectedEvent && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No event selected. Go back to the list and choose one.
        </Typography>
      )}

      {!isEditing && selectedEvent && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#d9b48b" }}>
                <Typography variant="overline" color="text.secondary" display="block">
                  Registration
                </Typography>
                <Typography fontWeight={700}>
                  {formatDate(selectedEvent.regDate)} to {formatDate(selectedEvent.regEndDate)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#d9b48b" }}>
                <Typography variant="overline" color="text.secondary" display="block">
                  Event Dates
                </Typography>
                <Typography fontWeight={700}>
                  {formatDate(selectedEvent.startDate)} to {formatDate(selectedEvent.endDate)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#d9b48b" }}>
                <Typography variant="overline" color="text.secondary" display="block">
                  Location
                </Typography>
                <Typography fontWeight={700}>{selectedEvent.location || "-"}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedEvent.address || ""}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#d9b48b" }}>
                <Typography variant="overline" color="text.secondary" display="block">
                  Capacity
                </Typography>
                <Typography fontWeight={700}>
                  {selectedEvent.maxRegistrationCount || "Open"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Waitlist {selectedEvent.waitlistCount || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {registrationSummary && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 2 }}>
              <Chip label={`Total ${registrationSummary.total || 0}`} variant="outlined" />
              <Chip label={`Booked ${registrationSummary.registered || 0}`} color="success" variant="outlined" />
              <Chip label={`Waitlist ${registrationSummary.waitlist || 0}`} color="warning" variant="outlined" />
              <Chip label={`Checked In ${registrationSummary.checkedIn || 0}`} color="info" variant="outlined" />
              <Chip label={`Cancelled ${registrationSummary.cancelled || 0}`} color="error" variant="outlined" />
            </Stack>
          )}

          {!!selectedEvent.wog && (
            <Box sx={{ borderLeft: `3px solid ${GA_COLORS.primary}`, pl: 2, mb: 2, fontStyle: "italic" }}>
              {selectedEvent.wog}
            </Box>
          )}

          {!!selectedEvent.timings && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" display="block">
                Timings
              </Typography>
              <Typography>{selectedEvent.timings}</Typography>
            </Box>
          )}

          {!!selectedEvent.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" display="block">
                Description
              </Typography>
              <Typography>{selectedEvent.description}</Typography>
            </Box>
          )}

          {!!selectedEvent.contact && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" display="block">
                Contact
              </Typography>
              <Typography>{selectedEvent.contact}</Typography>
            </Box>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#d9b48b" }}>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Brochure
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onOpenBrochure?.(selectedEvent)}
                sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
              >
                {selectedEvent.brochurePath ? "View Brochure" : "Upload Brochure"}
              </Button>
            </Stack>
          </Paper>

          {!!selectedEvent.paymentModes?.length && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#d9b48b" }}>
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Payment Modes
              </Typography>
              <Stack spacing={1}>
                {selectedEvent.paymentModes.map((pm, index) => (
                  <Box key={pm.modeID || `pm-read-${index}`} sx={{ border: "1px solid #ecd6bd", borderRadius: 1, p: 1 }}>
                    <Typography fontWeight={700}>{pm.displayName || pm.modeType || "Mode"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pm.modeAttributes?.url || ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pm.modeAttributes?.remarks || pm.modeAttributes?.instructions || ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {!!selectedEvent.ageGroups?.length && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#d9b48b" }}>
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Age Groups
              </Typography>
              <Stack spacing={1}>
                {selectedEvent.ageGroups.map((group, index) => (
                  <Box key={group.slabID || `ag-read-${index}`} sx={{ border: "1px solid #ecd6bd", borderRadius: 1, p: 1 }}>
                    <Typography fontWeight={700}>{group.ageGroupName || "General"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Age {group.minAge} to {group.maxAge} | Rate {group.rate} {group.currency || "INR"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Max {group.maxCount || "Open"} | Waitlist {group.waitlistCount || 0}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          <ActionBar />
        </>
      )}

      {isEditing && (
      <Box component="form" id="event-master-form" onSubmit={onSubmit} sx={gaFormSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Type"
              select
              value={form.type}
              onChange={(e) => onFieldChange("type", e.target.value)}
              disabled={!isEditing}
            >
              {typeOptions.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Status"
              select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              disabled={!isEditing}
            >
              {statusOptions.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Reg Start"
              InputLabelProps={{ shrink: true }}
              value={form.regDate}
              onChange={(e) => onFieldChange("regDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Reg End"
              InputLabelProps={{ shrink: true }}
              value={form.regEndDate}
              onChange={(e) => onFieldChange("regEndDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Event Start"
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={(e) => onFieldChange("startDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Event End"
              InputLabelProps={{ shrink: true }}
              value={form.endDate}
              onChange={(e) => onFieldChange("endDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Short Description"
              value={form.shortDescription}
              onChange={(e) => onFieldChange("shortDescription", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description"
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location"
              value={form.location}
              onChange={(e) => onFieldChange("location", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Max Registration Count"
              value={form.maxRegistrationCount}
              onChange={(e) =>
                onFieldChange("maxRegistrationCount", e.target.value)
              }
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Waitlist Count"
              value={form.waitlistCount}
              onChange={(e) => onFieldChange("waitlistCount", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={form.address}
              onChange={(e) => onFieldChange("address", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Map URL"
              value={form.map}
              onChange={(e) => onFieldChange("map", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Social Media Link"
              value={form.socialMediaLink}
              onChange={(e) => onFieldChange("socialMediaLink", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Retreat Email"
              value={form.retreatEmail}
              onChange={(e) => onFieldChange("retreatEmail", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Word of God"
              value={form.wog}
              onChange={(e) => onFieldChange("wog", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Timings"
              value={form.timings}
              onChange={(e) => onFieldChange("timings", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Contact"
              value={form.contact}
              onChange={(e) => onFieldChange("contact", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.onlineRegRequired)}
                  onChange={(e) =>
                    onFieldChange("onlineRegRequired", e.target.checked)
                  }
                  disabled={!isEditing}
                />
              }
              label="Online Registration Required"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.emailsEnabled)}
                  onChange={(e) => onFieldChange("emailsEnabled", e.target.checked)}
                  disabled={!isEditing}
                />
              }
              label="Enable Email Notifications"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, color: GA_COLORS.heading, fontWeight: 700 }}>
              Age Groups
            </Typography>
          </Grid>

          {(form.ageGroups || []).map((ageGroup, index) => (
            <Grid item xs={12} key={ageGroup._key || index}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: "#d9b48b", backgroundColor: "#fffefc" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={ageGroup.ageGroupName || ""}
                      onChange={(e) =>
                        onAgeFieldChange(index, "ageGroupName", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Min Age"
                      value={ageGroup.minAge || 0}
                      onChange={(e) => onAgeFieldChange(index, "minAge", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Age"
                      value={ageGroup.maxAge || 0}
                      onChange={(e) => onAgeFieldChange(index, "maxAge", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate"
                      value={ageGroup.rate || 0}
                      onChange={(e) => onAgeFieldChange(index, "rate", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      label="Currency"
                      value={ageGroup.currency || "INR"}
                      onChange={(e) => onAgeFieldChange(index, "currency", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Count"
                      value={ageGroup.maxCount || ""}
                      onChange={(e) => onAgeFieldChange(index, "maxCount", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Waitlist"
                      value={ageGroup.waitlistCount || ""}
                      onChange={(e) => onAgeFieldChange(index, "waitlistCount", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(ageGroup.isActive)}
                          onChange={(e) =>
                            onAgeFieldChange(index, "isActive", e.target.checked)
                          }
                          disabled={!isEditing}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    {isEditing && (
                      <Button
                        color="error"
                        onClick={() => onRemoveAgeGroup(index)}
                        disabled={loading}
                        sx={{ ...actionButtonSx }}
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {isEditing && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={onAddAgeGroup}
                sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
              >
                Add Age Group
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, color: GA_COLORS.heading, fontWeight: 700 }}>
              Payment Modes
            </Typography>
          </Grid>

          {(form.paymentModes || []).map((paymentMode, index) => (
            <Grid item xs={12} key={paymentMode._key || index}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: "#d9b48b", backgroundColor: "#fffefc" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Mode Type"
                      value={paymentMode.modeType || ""}
                      onChange={(e) =>
                        onPaymentModeFieldChange(index, "modeType", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={paymentMode.displayName || ""}
                      onChange={(e) =>
                        onPaymentModeFieldChange(index, "displayName", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Instructions"
                      value={paymentMode.modeAttributes?.instructions || ""}
                      onChange={(e) =>
                        onPaymentModeAttributeChange(
                          index,
                          "instructions",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Mode URL"
                      value={paymentMode.modeAttributes?.url || ""}
                      onChange={(e) =>
                        onPaymentModeAttributeChange(index, "url", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      value={paymentMode.modeAttributes?.remarks || ""}
                      onChange={(e) =>
                        onPaymentModeAttributeChange(index, "remarks", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Sort Order"
                      value={paymentMode.sortOrder || ""}
                      onChange={(e) =>
                        onPaymentModeFieldChange(index, "sortOrder", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(paymentMode.isActive)}
                          onChange={(e) =>
                            onPaymentModeFieldChange(index, "isActive", e.target.checked)
                          }
                          disabled={!isEditing}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    {isEditing && (
                      <Button
                        color="error"
                        onClick={() => onRemovePaymentMode(index)}
                        disabled={loading}
                        sx={{ ...actionButtonSx }}
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {isEditing && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={onAddPaymentMode}
                sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
              >
                Add Payment Mode
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
      )}

      <Dialog
        open={showQrDialog}
        onClose={() => setShowQrDialog(false)}
        PaperProps={{ sx: { border: `1px solid ${GA_COLORS.border}` } }}
      >
        <DialogTitle>Guest Registration QR</DialogTitle>
        <DialogContent>
          {selectedEvent?.eventID ? (
            <Stack spacing={1} alignItems="center" sx={{ minWidth: 280 }}>
              <img
                src={buildQrImageUrl(guestLink)}
                alt="Guest registration QR code"
                style={{ width: 240, height: 240 }}
              />
              <Typography variant="body2" color="text.secondary">
                {guestLink}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => onCopyGuestLink?.(selectedEvent)}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Copy Link
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.open(buildQrImageUrl(guestLink), "_blank")}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Open QR
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintQrCode?.(selectedEvent)}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Print QR
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowQrDialog(false)}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showMultiPrintDialog}
        onClose={() => setShowMultiPrintDialog(false)}
        PaperProps={{ sx: { border: `1px solid ${GA_COLORS.border}` } }}
      >
        <DialogTitle>ID Label Printing</DialogTitle>
        <DialogContent>
          <Typography>
            Choose which registrations to print for{" "}
            <strong>{selectedEvent?.name}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "male")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for Male
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "female")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for Female
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "all")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for All
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowMultiPrintDialog(false)}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default EventDetailScreen;
